import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { setInterval, clearInterval } from 'worker-timers'
import { authorize, getUserData, logout } from '../services/firebase'
import { upsertUser, proTrialAccess } from '../services/api/user'
import { appVersion } from '../services/api'
import { startAnalyticsSession, endAnalyticsSession } from '../services/paywall-analytics'
import { reportLoadFailure } from '../services/report-failure'
import Preloader from '../components/Preloader/Preloader'
import { calculateProfileCompleteness, mutateUserData } from './auth-user-data'

// Firebase uids whose verified state we've already re-synced to the API in this
// page session. Kept in memory (not localStorage) and keyed per uid so it is not
// shared across accounts on a shared browser, and so it resets on a full reload —
// which lets a transient re-sync failure retry on the next load rather than being
// permanently suppressed. Idempotency of the welcome email itself is guaranteed
// server-side by the atomic welcome_email_sent claim.
const welcomeSyncedUids = new Set()

export const AuthContext = React.createContext({})

/**
 * Whether Firebase has a session that has not been turned into an `AuthContext`
 * value yet.
 *
 * `AuthContext` is null both for a signed-out visitor and for one who is signed
 * in but whose profile, subscription and workspace data are still being
 * fetched. Route guards have to tell those apart: treating the second as
 * "signed out" is what used to bounce a visitor arriving from the brand handoff
 * onto `/` in the middle of signing in, costing a redirect and a whole extra
 * app boot on top of the wait they were already in.
 *
 * `hydrateFailed`: Firebase has a session but turning it into a user threw.
 * Guards must offer `retryHydrate` here, not redirect: `/` sees the Firebase
 * session and sends the visitor straight back, which looped (NOLAN-STUDIO-22W).
 */
export const AuthStatusContext = React.createContext({ hydrating: false, hydrateFailed: false, retryHydrate: () => {} })

export const AuthProvider = ({ skipLoading, children }) => {
  const location = window.location
  const [user, setUser] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [hydrating, setHydrating] = useState(false)
  const [hydrateError, setHydrateError] = useState(null)
  const authRef = useRef(null)
  const hydrateRef = useRef(null)
  const hydrateRun = useRef(0)
  const refreshTimer = useRef(null)

  const refreshToken = async () => {
    const user = await getUserData()
    if (user) {
      const idToken = await user.getIdToken(true)
      window.localStorage.setItem('token', idToken)
    }
  }

  const updatePaymentInfo = async (storedUser) => {
    const userData = await mutateUserData(user || storedUser)
    if (!userData.updatePaymentInfo) {
      userData.updatePaymentInfo = () => updatePaymentInfo(storedUser)
    }
    if (!userData.refreshToken) {
      userData.refreshToken = () => refreshToken()
    }
    setUser(userData)
  }

  const updateProfileInfo = (data, storedUser) => {
    storedUser.profile = {
      ...storedUser.profile,
      ...data
    }
    storedUser.profileCompleteness = calculateProfileCompleteness(storedUser.profile)
    setUser(storedUser)
  }

  const stopTokenRefresh = () => {
    if (refreshTimer.current) clearInterval(refreshTimer.current)
    refreshTimer.current = null
  }

  // Turns a Firebase session into the `AuthContext` user. Runs on every auth
  // state change, and again from `retryHydrate`.
  const hydrate = async (firebaseUser) => {
    const run = ++hydrateRun.current
    const isCurrent = () => run === hydrateRun.current
    stopTokenRefresh()
    setHydrateError(null)
    // Everything below is awaited, so between here and `setUser` there is
    // a stretch — several API calls long on a slow connection — where
    // Firebase has a session but `AuthContext` is still null. Route
    // guards read `hydrating` to avoid mistaking that for signed out.
    setHydrating(firebaseUser != null)
    try {
      if (firebaseUser != null) {
        const firebaseUserData = firebaseUser.toJSON()
        const idToken = await firebaseUser.getIdToken()
        let storedUser = {
          ...firebaseUserData,
          idToken
        }
        // No token on this origin means this is the first sign-in *here* —
        // a brand-new account, or an existing one arriving through the
        // brand handoff, which lands on a studio origin that has never
        // held a session. Both need the API-side account provisioned
        // before the rest of the boot can read it.
        const token = window.localStorage.getItem('token')
        if (!token) {
          if (location.pathname === '/e3ud0s') {
            await proTrialAccess(storedUser)
          } else {
            await upsertUser(storedUser)
          }
          // This first-sign-in call already carried the current verified
          // state to the API. Record it per uid so we don't redundantly
          // re-sync the same account again this session. Email/password
          // signups start unverified here and are synced later, once the
          // email is confirmed (see verified re-sync below).
          if (firebaseUserData.emailVerified) {
            welcomeSyncedUids.add(firebaseUserData.uid)
          }
          // once user authorized and there is no any app version
          // it means it's already updated and no need validate and show banner.
          // we are adding the latest version in local storage
          // eslint-disable-next-line no-undef
          const currentVersion = localStorage.getItem('currentVersion')
          if (!currentVersion) {
            try {
              const server = await appVersion.getCurrentVersion()
              // eslint-disable-next-line no-undef
              localStorage.setItem('currentVersion', server.version)
              // eslint-disable-next-line no-undef
              localStorage.setItem('currentVersionDate', server.date)
            } catch (e) {
              console.warn('cant set version', e)
            }
          }
        }
        // eslint-disable-next-line no-undef
        localStorage.setItem('token', idToken)
        // Provisioning above and this now run in the same pass, so the
        // `welcomeSyncedUids` guard is what stops a first sign-in
        // upserting twice. A returning session skipped provisioning
        // entirely and falls through to here on its own.
        //
        // When the user has just verified their email (the verify banner
        // reloads the page on confirmation) the API still thinks the
        // account is unverified, so re-sync once to trigger the welcome
        // email. The API sends it at most once via an atomic claim.
        if (firebaseUserData.emailVerified && !welcomeSyncedUids.has(firebaseUserData.uid)) {
          try {
            await upsertUser(storedUser)
            welcomeSyncedUids.add(firebaseUserData.uid)
          } catch (e) {
            console.warn('cant sync verified user', e)
          }
        }
        storedUser = await mutateUserData(storedUser)
        // A newer auth event or retry owns the state now.
        if (!isCurrent()) return
        storedUser.logout = async () => {
          await logout()
          setUser(null)
        }
        storedUser.updatePaymentInfo = () => updatePaymentInfo(storedUser)
        storedUser.updateProfileInfo = (data) => updateProfileInfo(data, storedUser)
        storedUser.refreshToken = () => refreshToken()
        setUser(() => {
          return { ...storedUser }
        })
        refreshTimer.current = setInterval(() => {
          refreshToken()
        }, 30 * 60 * 1000)
      }
    } catch (error) {
      // Settle rather than gate the tree forever; guards show a retry screen.
      console.error('cant hydrate signed-in user', error)
      if (!isCurrent()) return
      reportLoadFailure('auth', error)
      setHydrateError(error)
    } finally {
      if (isCurrent()) {
        setHydrating(false)
        setLoaded(true)
      }
    }
  }
  hydrateRef.current = hydrate

  // Listen to the Firebase Auth state and set the local state.
  useEffect(() => {
    let unregisterAuthObserver
    authorize()
      .then((auth) => {
        authRef.current = auth
        unregisterAuthObserver = auth.onAuthStateChanged((user) => {
          // Every call site that signs a user out (Preloader,
          // UserProfileAvatar, VerifyEmail, this file's own `logout`, and the
          // logout container) funnels through Firebase sign-out, so this
          // observer is the one place that is guaranteed to fire for all of
          // them. Kept out of `hydrate` so a retry doesn't restart the session.
          if (user != null) {
            startAnalyticsSession()
          } else {
            endAnalyticsSession()
          }
          return hydrateRef.current(user)
        })
      })
      .catch(error => {
        console.error(error)
      })

    return () => {
      unregisterAuthObserver && unregisterAuthObserver()
      stopTokenRefresh()
    } // Make sure we un-register Firebase observers when the component unmounts.
  }, [])

  // In place, no reload: `hydrating` covers the wait.
  const retryHydrate = useCallback(() => hydrateRef.current(authRef.current?.currentUser ?? null), [])

  // Stable identity, so a provider re-render doesn't invalidate every guard
  // reading this context.
  const hydrateFailed = hydrateError != null
  const authStatus = useMemo(() => ({ hydrating, hydrateFailed, retryHydrate }), [hydrating, hydrateFailed, retryHydrate])

  // Until the first auth callback lands this used to render `false` — nothing
  // at all — so the app was a blank page for as long as Firebase took to
  // restore a session. The boot screen in `public/index.html` is taken down as
  // soon as React mounts, so this has to paint something itself.
  if (!loaded && !skipLoading) {
    return <Preloader />
  }

  return (
    <AuthStatusContext.Provider value={authStatus}>
      <AuthContext.Provider value={user}>{children}</AuthContext.Provider>
    </AuthStatusContext.Provider>
  )
}
